import {
  buttonClasses,
  ButtonClasses,
  styled,
  useThemeProps,
} from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';
import generateUtilityClass from '@mui/utils/generateUtilityClass';
import { getToken } from '@workiva/unify-tokens';
import clsx from 'clsx';
import React from 'react';
import Button, {
  ButtonProps,
  ButtonTypeMap,
} from 'src/components/Button/Button';
import { getLinkColorStyles } from 'src/components/Link/linkStyleOverrides';
// eslint-disable-next-line deprecation/deprecation
import { getThemePalette } from 'src/styles/utils';
import { activeButtonRoleAriaAttributes } from 'src/utils/cssSelectorConstants';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import { wsdButtonDefaultProps } from 'src/wsd/components/Button/wsdButtonDefaultProps';
import useLegacyWsdStyles from 'src/wsd/styles/context/useLegacyWsdStyles';
import wkTheme from 'src/wsd/styles/theme/wkTheme';

// eslint-disable-next-line deprecation/deprecation
export interface WsdLinkButtonOwnProps {
  classes?: Partial<ButtonClasses>;
}

export type WsdLinkButtonTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent']
> = ExtendTypeMap<
  {
    props: Omit<
      ButtonTypeMap<AdditionalProps, RootComponent>['props'],
      | 'color'
      | 'variant'
      | 'disableRipple'
      | 'disableElevation'
      | 'loading'
      | 'loadingPosition'
      | 'loadingIndicator'
    >;
    defaultComponent: ButtonTypeMap<
      AdditionalProps,
      RootComponent
    >['defaultComponent'];
  },
  AdditionalProps & WsdLinkButtonOwnProps,
  RootComponent
>;

export type WsdLinkButtonProps<
  RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  WsdLinkButtonTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

const WsdLinkButtonRoot = styled(Button, {
  name: 'MuiLinkButton',
  slot: 'Root',
  shouldForwardProp: (propName) => propName !== 'isWsdLinkButton',
})<ButtonProps & { isWsdLinkButton: boolean }>(function WsdLinkButtonRoot({
  theme: themeContext,
  isWsdLinkButton,
}) {
  // eslint-disable-next-line deprecation/deprecation
  const theme = isWsdLinkButton ? wkTheme : themeContext;
  // eslint-disable-next-line deprecation/deprecation
  const palette = getThemePalette(theme);
  if (!isWsdLinkButton) {
    return {
      ...theme.typography.body1,

      [`&.${buttonClasses.colorInherit}`]: {
        textDecoration: 'underline',
        ':hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline',
        },
        ...theme.typography.body1,
        ...getLinkColorStyles(),
        [activeButtonRoleAriaAttributes]: {
          backgroundColor: getToken('Core/Link/link-primary'),
          color: getToken('Core/Text/text-onColor'),
          textDecoration: 'none',
        },
      },
    };
  }

  return {
    // eslint-disable-next-line deprecation/deprecation
    color: palette.blue?.[900],
    transition: 'none',
    ':hover': {
      // eslint-disable-next-line deprecation/deprecation
      color: palette.blue?.main,
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
    [activeButtonRoleAriaAttributes]: {
      // eslint-disable-next-line deprecation/deprecation
      backgroundColor: palette.blue?.[900],
      color: '#fff',
    },
    ':disabled': {
      // eslint-disable-next-line deprecation/deprecation
      color: palette.blue?.[900],
    },
  };
});

export const WsdLinkButton = forwardRefToOverridableComponent<
  WsdLinkButtonTypeMap,
  WsdLinkButtonProps
>(function WsdLinkButton(inProps, forwardedRef) {
  const legacyWsdStyleSupport = useLegacyWsdStyles();
  const unifyProps = useThemeProps({ props: inProps, name: 'MuiButton' });
  const defaultWsdProps = wsdButtonDefaultProps;
  const wsdProps = { ...defaultWsdProps, ...inProps };
  const props = legacyWsdStyleSupport ? wsdProps : unifyProps;
  const { children, className: classNameProp, ...rest } = props;
  const className = clsx(
    generateUtilityClass('MuiLinkButton', 'root'),
    classNameProp,
    props.classes?.root
  );

  return (
    <WsdLinkButtonRoot
      {...rest}
      isWsdLinkButton={legacyWsdStyleSupport}
      ref={forwardedRef}
      className={className}
      disableElevation={true}
      disableRipple={true}
      variant="text"
      color="inherit"
    >
      {children}
    </WsdLinkButtonRoot>
  );
});

export default WsdLinkButton;
